import { ReactElement, useEffect } from 'react';
import { UIEmptyState, useUINav } from '@/ui';
import { useCompanyContext, useMember } from '@/companies/providers';
import { usePagesTranslation } from '../../hooks';
import { useEditProfileAction } from '@/companies/actions';
import { hasFullAccess } from '@/companies/util/company-user-access';
import {
    MemberDescriptionSettingsSection,
    MemberMedicalSettingsSection,
    MemberNextOfKinSettingsSection
} from '@/companies/components';

export function MemberIdEmergencyInformationSettingsPage(): ReactElement {
    const { t } = usePagesTranslation();
    const { company } = useCompanyContext();
    const { companyUser } = useMember();
    const { isAllowed: canEditProfile, reason: editProfileReason } =
        useEditProfileAction({ user: companyUser });

    const name = companyUser.joined
        ? `${companyUser.firstName} ${companyUser.lastName}`
        : companyUser.email;

    const { setParts } = useUINav();
    useEffect(() => {
        setParts([
            { text: t('shared.labels.settings') },
            {
                text: t('shared.labels.members'),
                link: `/company/${company.id}/settings/members`
            },
            { text: name },
            { text: t('shared.labels.emergency_info') }
        ]);
    }, [setParts, company.id, name, t]);

    // The API withholds this member's description, medical details and next of
    // kin from viewers whose only access is a shared team. Say so rather than
    // showing three empty sections — the reader can then go and ask for the
    // access they need. Past this point the sections are guaranteed their data.
    if (!hasFullAccess(companyUser)) {
        return (
            <UIEmptyState
                emoji="🔒"
                title={t(
                    'pages.member_id_emergency_information_settings.hidden_title'
                )}
                description={t(
                    'pages.member_id_emergency_information_settings.hidden_description'
                )}
            />
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <MemberDescriptionSettingsSection
                companyUser={companyUser}
                canEdit={canEditProfile}
                editDisabledReason={editProfileReason}
            />
            <MemberMedicalSettingsSection
                companyUser={companyUser}
                canEdit={canEditProfile}
                editDisabledReason={editProfileReason}
            />
            <MemberNextOfKinSettingsSection
                companyUser={companyUser}
                canEdit={canEditProfile}
                editDisabledReason={editProfileReason}
            />
        </div>
    );
}
